import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { ShopBySchool } from "../components/ShopBySchool";

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(true);
  const activeCategory = searchParams.get("category") || "All";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(["All", ...r.data])).catch(() => {});
  }, []);

  useEffect(() => {
    setSearch(searchParams.get("q") || "");
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = {};
    if (activeCategory !== "All") params.category = activeCategory;
    if (search) params.search = search;
    api.get("/products", { params })
      .then((r) => { if (active) setProducts(r.data); })
      .catch(() => { if (active) setProducts([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [activeCategory, search]);

  const setCategory = (cat) => {
    if (cat === "All") setSearchParams({});
    else setSearchParams({ category: cat });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10" data-testid="shop-page">
      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-2">
        {activeCategory === "All" ? "All Products" : activeCategory}
      </h1>
      <p className="text-muted-foreground mb-8">{products.length} finds waiting for you</p>

      <div className="flex flex-col lg:flex-row gap-4 mb-10">
        <div className="relative flex-1 max-w-md">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full bg-white border-2 border-ink rounded-full pl-12 pr-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium"
            data-testid="search-input"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SlidersHorizontal className="w-5 h-5 hidden sm:block" />
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} data-testid={`filter-${c}`}
              className={`px-4 py-2 rounded-full border-2 border-ink font-bold text-sm transition-all ${
                activeCategory === c ? "bg-brand text-white shadow-hard-sm" : "bg-white hover:bg-brand-yellow"
              }`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {activeCategory === "Collegiate" && <ShopBySchool embedded />}

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] rounded-3xl border-2 border-ink bg-secondary animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-24" data-testid="no-products">
          <p className="text-2xl font-black mb-2">No finds here 😢</p>
          <p className="text-muted-foreground">Try a different search or category.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
